"use client";

import { FC, useState } from "react";
import EmptyCaptionsTrack from "./EmptyCaptionsTrack";
import Typography from "./Typography";
import { MediaBlockData } from "./blocks/MediaBlock";

type PresentationCardProps = {
  title: string;
  description: string;
  media: MediaBlockData;
  index: number;
  playingVideoIndex: number | null;
  setPlayingVideoIndex: (index: number | null) => void;
};

const PresentationCard: FC<PresentationCardProps> = ({
  title,
  description,
  media,
  index,
  playingVideoIndex,
  setPlayingVideoIndex,
}) => {
  const [progress, setProgress] = useState<number>(0);

  const handleTimeUpdate = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    const video = e.currentTarget;
    const progressPercentage = (video.currentTime / video.duration) * 100;
    setProgress(progressPercentage);
  };

  const handleMouseEnter = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    const container = e.currentTarget;
    const video = container.querySelector("video") as HTMLVideoElement;
    if (video) {
      setPlayingVideoIndex(index); // Set the current playing video index
      video.play();
    }
  };

  const handleMouseLeave = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    const container = e.currentTarget;
    const video = container.querySelector("video") as HTMLVideoElement;
    if (video) {
      video.pause();
      video.currentTime = 0;
      setProgress(0);
      setPlayingVideoIndex(null); // Reset the playing video index
    }
  };

  return (
    <div
      className="flex-1 text-start group"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="bg-slate-150 rounded-2xl flex items-center justify-center px-6 pt-6 overflow-hidden relative">
        {/* `preload="metadata"` (instead of `"none"`) so the browser fetches
            just enough video bytes (~few hundred KB, lazy after page is
            interactive) to render the first frame as a built-in poster.
            Without this, the card shows an empty grey rectangle until the
            user hovers — which both looks dead and is what made the
            previous lazy-load approach feel broken. The spec-correct
            alternative would be a real `poster` JPEG on the CDN, but
            metadata-preload gets us 95% of the same UX with zero new
            assets. */}
        <video
          src={media.src}
          loop
          muted
          playsInline
          preload="metadata"
          disableRemotePlayback
          className="rounded-b-none rounded-t-lg w-full"
          onTimeUpdate={handleTimeUpdate}
        >
          <EmptyCaptionsTrack />
        </video>
        {playingVideoIndex === index && (
          <div
            className="absolute bottom-0 left-0 h-1 bg-slate-950"
            style={{ width: `${progress}%` }}
          ></div>
        )}
      </div>
      <Typography variant="subtitle1" className="mt-4 block">
        {title}
      </Typography>
      <Typography variant="body2" className="mt-2 text-slate-500">
        {description}
      </Typography>
    </div>
  );
};

export default PresentationCard;
